import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { assistantService, type AssistantTurn, type AssistantRecommendation } from '@/services/ai/assistantService';

interface ChatMessage {
  role: 'user' | 'assistant';
  text: string;
  // Attached to assistant turns that recommend an app / offer to build a workflow.
  recommendation?: AssistantRecommendation | null;
  action?: 'chat' | 'navigate' | 'build_workflow';
  workflowSeed?: string | null;
  // Local error notices are shown in the thread but never sent back as history.
  isError?: boolean;
}

interface SnapfyAssistantProps {
  // 'embedded' = home hero card; 'panel' = floating global helper.
  variant?: 'embedded' | 'panel';
  // Called after a recommendation navigates away (lets the floating panel close).
  onNavigated?: () => void;
}

// The Snapfy Assistant chat: understands the user's goal and recommends the best
// Snapfy app (Lookbook, Canvas, Workflow, …). The LLM call lives behind the Laravel
// API (assistantService), never an AI SDK in the front. Reused by the home hero and
// the global floating helper.
const SnapfyAssistant: React.FC<SnapfyAssistantProps> = ({ variant = 'embedded', onNavigated }) => {
  const { t } = useTranslation('assistant');
  const navigate = useNavigate();
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const listRef = useRef<HTMLDivElement>(null);
  // Synchronous in-flight guard — `loading` updates async, so a fast double Enter
  // could otherwise fire two POSTs with the same history.
  const sendingRef = useRef(false);

  useEffect(() => {
    listRef.current?.scrollTo({ top: listRef.current.scrollHeight, behavior: 'smooth' });
  }, [messages, loading]);

  const goToApp = (rec: AssistantRecommendation, action: ChatMessage['action'], seed?: string | null) => {
    if (action === 'build_workflow') {
      // Seed the Workflow agent so it auto-builds the graph for this need.
      navigate('/workflow', { state: { assistantSeed: seed || '' } });
    } else {
      navigate(rec.route);
    }
    onNavigated?.();
  };

  const send = async () => {
    const text = input.trim();
    if (!text || sendingRef.current) return;
    sendingRef.current = true;

    // History excludes local error notices — only real turns are sent upstream.
    const history: AssistantTurn[] = messages
      .filter((m) => !m.isError)
      .map((m) => ({ role: m.role, text: m.text }));
    setMessages((prev) => [...prev, { role: 'user', text }]);
    setInput('');
    setLoading(true);
    try {
      const res = await assistantService.chat(history, text);
      setMessages((prev) => [
        ...prev,
        {
          role: 'assistant',
          text: res.reply || '…',
          recommendation: res.recommendation,
          action: res.action,
          workflowSeed: res.workflowSeed,
        },
      ]);
    } catch {
      setMessages((prev) => [...prev, { role: 'assistant', text: t('error'), isError: true }]);
    } finally {
      setLoading(false);
      sendingRef.current = false;
    }
  };

  const isPanel = variant === 'panel';

  return (
    <div className={`flex flex-col ${isPanel ? 'h-full' : 'h-[clamp(420px,60vh,640px)] rounded-2xl snapfy-glass'}`}>
      {/* Messages / empty state */}
      <div ref={listRef} className="flex-1 overflow-y-auto px-4 py-4 space-y-3">
        {messages.length === 0 && !loading && (
          <div className="h-full flex flex-col items-center justify-center text-center px-4">
            <div className="w-12 h-12 mb-4 rounded-2xl bg-snapfy-gradient flex items-center justify-center shadow-glow">
              <svg className="w-6 h-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
              </svg>
            </div>
            <h2 className={`font-bold text-snapfy-animated-gradient pb-1 leading-tight ${isPanel ? 'text-lg' : 'text-3xl md:text-4xl'}`}>
              {t('emptyTitle')}
            </h2>
            <p className="mt-2 text-sm text-snapfy-text-secondary max-w-md">{t('emptySubtitle')}</p>
          </div>
        )}

        {messages.map((m, i) => (
          <div key={i} className={m.role === 'user' ? 'flex justify-end' : 'flex flex-col items-start'}>
            <div
              className={`max-w-[85%] rounded-card px-3 py-2 text-sm leading-relaxed whitespace-pre-wrap ${
                m.role === 'user'
                  ? 'bg-snapfy-gradient text-white self-end'
                  : 'bg-snapfy-inner text-snapfy-text-primary border border-snapfy-border'
              }`}
            >
              {m.text}
            </div>
            {m.role === 'assistant' && m.recommendation && (m.action === 'navigate' || m.action === 'build_workflow') && (
              <button
                onClick={() => goToApp(m.recommendation!, m.action, m.workflowSeed)}
                className="mt-2 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-button text-xs font-bold bg-snapfy-gradient text-white shadow-glow hover:opacity-90 transition-opacity"
              >
                <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                </svg>
                {m.action === 'build_workflow'
                  ? t('buildWorkflowCta')
                  : t('openAppCta', { app: m.recommendation.label })}
              </button>
            )}
          </div>
        ))}

        {loading && (
          <div className="flex justify-start">
            <div className="bg-snapfy-inner text-snapfy-text-secondary border border-snapfy-border rounded-card px-3 py-2 text-xs flex items-center gap-2">
              <svg className="w-3 h-3 animate-spin" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
              </svg>
              {t('thinking')}
            </div>
          </div>
        )}
      </div>

      {/* Input */}
      <div className="border-t border-snapfy-border p-3">
        <div className="flex items-end gap-2">
          <textarea
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                send();
              }
            }}
            rows={isPanel ? 2 : 1}
            placeholder={t('inputPlaceholder')}
            className="flex-1 resize-none bg-snapfy-bg border border-snapfy-border rounded-xl p-2.5 text-sm text-snapfy-text-primary focus:outline-none focus:ring-1 focus:ring-[#7063f7] placeholder-snapfy-text-secondary"
          />
          <button
            onClick={send}
            disabled={loading || !input.trim()}
            className="inline-flex items-center justify-center px-4 py-2.5 rounded-button text-sm font-bold bg-snapfy-gradient text-white shadow-glow hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed transition-opacity"
          >
            {t('send')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default SnapfyAssistant;
